import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function PostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', company: '', location: '', jobType: 'overseas-market',
    category: 'legal', salary: '', description: '', requirements: '', contactInfo: '',
    publishDate: new Date().toISOString().split('T')[0],
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('form'); // 'form' | 'smart'
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: 60, textAlign: 'center' }}>
        <p>请先 <a href="/login" style={{ color: 'var(--primary)' }}>登录</a> 后发布岗位</p>
      </div>
    );
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await api.post('/jobs', form);
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || '发布失败');
    }
    setSubmitting(false);
  };

  const handleSmartParse = async () => {
    if (!rawText.trim()) return;
    setParsing(true);
    setError('');
    try {
      const res = await api.post('/jobs/smart-parse', { rawText });
      setForm(prev => ({ ...prev, ...res.data.parsed }));
      setMode('form');
    } catch (err) {
      setError(err.response?.data?.error || '解析失败');
    }
    setParsing(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    const validExts = ['.pdf', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const name = file.name || '';
    const ext = name.includes('.') ? name.substring(name.lastIndexOf('.')).toLowerCase() : '';
    const isImage = (file.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
    if (!validTypes.includes(file.type) && !validExts.includes(ext) && !isImage) {
      setError('不支持的文件格式，请上传 PDF、Word (.docx)、文本或图片文件');
      return;
    }
    setParsing(true);
    setError('');
    setFileName(file.name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // 注意：不要手动设置 Content-Type，axios/浏览器会自动带上 boundary。
      // 手动设置会导致部分手机浏览器上传失败。
      const res = await api.post('/jobs/smart-parse-file', formData, {
        timeout: 180000, // 手机端图片 OCR 可能需要较长时间
      });
      if (res.data.rawText) setRawText(res.data.rawText);
      setForm(prev => ({ ...prev, ...res.data.parsed }));
      setMode('form');
    } catch (err) {
      setError(err.response?.data?.error || '文件解析失败');
    }
    setParsing(false);
    setFileName('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };

  // 粘贴：支持直接 Cmd/Ctrl+V 图片（截图 OCR）或 Finder 复制的文件
  const handlePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    // 优先看 files（Finder 复制的 PDF/DOCX/TXT 或截图工具写入的图片）
    if (cd.files && cd.files.length > 0) {
      e.preventDefault();
      handleFileUpload(cd.files[0]);
      return;
    }
    // 再看 items（某些浏览器里截图粘贴走这条路径）
    const items = cd.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file' && it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          const named = new File(
            [file],
            file.name && file.name !== 'image.png' ? file.name : `pasted-${Date.now()}.png`,
            { type: file.type || 'image/png' }
          );
          handleFileUpload(named);
          return;
        }
      }
    }
    // 否则是纯文本粘贴，交给 textarea 默认行为
  };

  return (
    <div className="container" style={{ maxWidth: 700, paddingTop: 20, paddingBottom: 60 }}>
      <div className="page-header">
        <h1 className="page-title">发布岗位</h1>
        <p className="page-subtitle">分享涉外岗位信息，帮助更多人找到全球机会</p>
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {[['smart', '🤖 智能导入'], ['form', '📝 手动填写']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: mode === key ? 700 : 400, fontSize: '1rem',
              borderBottom: mode === key ? '2px solid var(--primary)' : '2px solid transparent',
              color: mode === key ? 'var(--primary)' : 'var(--text-light)',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Smart import mode */}
      {mode === 'smart' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: 12 }}>
            上传文件或粘贴招聘信息，系统会自动解析成结构化数据。
          </p>

          {/* Drag & drop / file upload zone（用 <label> 包裹以兼容手机） */}
          <label
            htmlFor="file-upload-input"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              display: 'block',
              border: `2px dashed ${dragging ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: 12,
              padding: '32px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? 'rgba(37,99,235,0.04)' : 'var(--bg-light, #fafafa)',
              transition: 'all 0.2s',
              marginBottom: 16,
              WebkitTapHighlightColor: 'rgba(37,99,235,0.15)',
              minHeight: 120,
            }}
          >
            <input
              id="file-upload-input"
              type="file"
              accept=".pdf,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.bmp,image/*"
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
              onChange={e => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }}
            />
            {parsing && fileName ? (
              <div style={{ color: 'var(--primary)', fontSize: '0.95rem' }}>
                📄 正在解析 {fileName}...（图片 OCR 约 30–60 秒）
              </div>
            ) : (
              <>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>📎</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, marginBottom: 4 }}>
                  点击选择文件 / 拖拽到这里
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                  支持 PDF、Word (.docx)、文本、JPG/PNG 截图（自动 OCR）
                </div>
              </>
            )}
          </label>

          <div style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: '0.85rem', margin: '12px 0' }}>
            — 或者直接粘贴文字 —
          </div>

          <textarea
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            onPaste={handlePaste}
            placeholder={`粘贴 JD 文字、或直接 Cmd/Ctrl+V 粘贴截图（自动 OCR）、PDF/Word 文件...\n\n例如：\n高级跨境合规经理\n公司：字节跳动\n地点：新加坡\n薪资：60-90万/年\n岗位描述：...\n联系方式：hr@example.com`}
            style={{
              width: '100%', minHeight: 160, padding: 16, borderRadius: 8,
              border: '1px solid var(--border)', fontSize: '0.95rem',
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <button
            className="btn btn-primary"
            onClick={handleSmartParse}
            disabled={parsing || !rawText.trim()}
            style={{ marginTop: 12, width: '100%', padding: '12px 0' }}
          >
            {parsing ? '解析中...' : '🔍 智能解析并填充表单'}
          </button>
        </div>
      )}

      {/* Form mode (also shown after smart parse) */}
      {mode === 'form' && (
        <form onSubmit={handleSubmit}>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>基本信息</h3>

            <div className="form-group">
              <label className="form-label">岗位名称 <span className="required">*</span></label>
              <input className="form-input" name="title" value={form.title} onChange={handleChange} placeholder="如：高级合规经理（东南亚）" required />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">公司名称 <span className="required">*</span></label>
                <input className="form-input" name="company" value={form.company} onChange={handleChange} placeholder="公司或机构名称" required />
              </div>
              <div className="form-group">
                <label className="form-label">工作地点 <span className="required">*</span></label>
                <input className="form-input" name="location" value={form.location} onChange={handleChange} placeholder="如：新加坡、上海（管理海外）" required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">岗位类型 <span className="required">*</span></label>
                <select className="form-select" name="jobType" value={form.jobType} onChange={handleChange}>
                  <option value="overseas-market">国内负责海外市场</option>
                  <option value="global-market">国内负责全球市场</option>
                  <option value="foreign-domestic">国内外资企业</option>
                  <option value="expat">外派</option>
                  <option value="local-hire">海外本地招聘（需中国背景）</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">岗位类别 <span className="required">*</span></label>
                <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                  <option value="legal">法律合规</option>
                  <option value="finance">财务审计</option>
                  <option value="business">商务拓展</option>
                  <option value="engineering">工程技术</option>
                  <option value="hr">人力资源</option>
                  <option value="supply-chain">供应链</option>
                  <option value="other">其他</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">薪资范围</label>
                <input className="form-input" name="salary" value={form.salary} onChange={handleChange} placeholder="如：40-60万/年" />
              </div>
              <div className="form-group">
                <label className="form-label">发布日期</label>
                <input className="form-input" type="date" name="publishDate" value={form.publishDate} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>详细信息</h3>

            <div className="form-group">
              <label className="form-label">岗位描述 <span className="required">*</span></label>
              <textarea className="form-textarea" name="description" value={form.description} onChange={handleChange}
                placeholder="详细描述岗位职责、工作内容、团队情况等..." required style={{ minHeight: 160 }} />
            </div>

            <div className="form-group">
              <label className="form-label">任职要求</label>
              <textarea className="form-textarea" name="requirements" value={form.requirements} onChange={handleChange}
                placeholder="学历要求、工作经验、语言能力、专业技能等..." />
            </div>

            <div className="form-group">
              <label className="form-label">联系方式</label>
              <input className="form-input" name="contactInfo" value={form.contactInfo} onChange={handleChange}
                placeholder="邮箱、微信或其他联系方式" />
              <div className="form-hint">登录用户可查看联系方式</div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={submitting}>
            {submitting ? '发布中...' : '发布岗位'}
          </button>
        </form>
      )}
    </div>
  );
}

export default PostJob;
